/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;

  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to save offline token (imported dynamically to avoid circular deps)
const saveOfflineTokenToStorage = async (tier: 'pro' | 'basic' | 'free') => {
  const DB_NAME = 'vesper-offline';
  const DB_VERSION = 1;
  const TOKEN_STORE = 'offline-token';
  
  return new Promise<boolean>((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => resolve(false);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(TOKEN_STORE)) {
        db.createObjectStore(TOKEN_STORE, { keyPath: 'id' });
      }
    };
    
    request.onsuccess = () => {
      try {
        const db = request.result;
        const transaction = db.transaction(TOKEN_STORE, 'readwrite');
        const store = transaction.objectStore(TOKEN_STORE);
        
        const now = new Date();
        const token = {
          id: 'current',
          subscriptionTier: tier,
          validatedAt: now,
          expiresAt: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
        };
        
        store.put(token);
        resolve(true);
      } catch {
        resolve(false);
      }
    };
  });
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Check subscription from local database and save offline token
  const checkAndSaveOfflineToken = useCallback(async (userId: string, userEmail?: string) => {
    try {
      // First try: Query by user_id
      let localSub = null;
      
      const result1 = await supabase
        .from("subscriptions")
        .select("plan")
        .eq("user_id", userId)
        .maybeSingle();
      
      localSub = result1.data;
      
      // Fallback: Query by email via profiles join (handles identity linking issues)
      if (!localSub && userEmail) {
        const result2 = await supabase
          .from("subscriptions")
          .select(`
            plan,
            profiles!subscriptions_user_id_fkey(email)
          `)
          .eq("profiles.email", userEmail)
          .maybeSingle();
        
        localSub = result2.data;
      }
      
      const tier = localSub?.plan || 'free';
      await saveOfflineTokenToStorage(tier as 'pro' | 'basic' | 'free');
    } catch (err) {
      console.error("Failed to save offline token:", err);
    }
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Save offline token when user logs in
        if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          // Use setTimeout to avoid blocking
          setTimeout(() => {
            checkAndSaveOfflineToken(session.user.id, session.user.email);
          }, 1000);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Also save token on initial load if logged in
      if (session?.user) {
        setTimeout(() => {
          checkAndSaveOfflineToken(session.user.id, session.user.email);
        }, 1000);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAndSaveOfflineToken]);

  const signUp = async (email: string, password: string, name?: string) => {
    // Redirect to dashboard after email verification
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          name: name || "",
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error };
  };



  const signOut = async () => {
    await supabase.auth.signOut({ scope: 'global' });
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,

    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
